import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Breadcrumb from '../components/breadcrumb';
import { reportsService, ReportType } from '../services/reports.service';
import { FileBarChart, BarChart2, Users, ChevronRight } from 'lucide-react';
import { ReportsPageSkeleton } from '../components/Skeleton';

const Reports: React.FC = () => {
  const navigate = useNavigate();
  const [reportTypes, setReportTypes] = useState<ReportType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch report types on component mount
  useEffect(() => {
    const fetchReportTypes = async () => {
      try {
        const types = await reportsService.getReportTypes();
        setReportTypes(types);
      } catch (err) {
        setError('Failed to fetch report types');
        console.error('Error fetching report types:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchReportTypes();
  }, []);


  const handleCardClick = (index: number) => {
    navigate(`/reports/${index + 1}`, { state: { reportType: reportTypes[index].reportType } });
  };

  return (
    <div id="reports-container" className="bg-gray-50 p-3 sm:p-4 md:p-6">
      <div className="">
        <div className="mb-4 sm:mb-6">
          <Breadcrumb title="Reports" />
        </div>

        {loading ? (
          <ReportsPageSkeleton />
        ) : (
          <>
            <div id="reports-content" className="bg-white rounded-[12px] shadow overflow-hidden p-3 sm:p-4 mb-4 min-h-[70vh]">
              {/* Report Templates Grid */}
              {error ? (
                <div id="reports-error" className="text-red-500 text-center p-4">{error}</div>
              ) : (
                <div id="reports-grid" className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-4 mb-6">
                  {reportTypes.map((reportType, index) => {
                    // Select icon based on report type
                    let ReportIcon = FileBarChart;
                    if (reportType.reportType.toLowerCase().includes('analytics')) {
                      ReportIcon = BarChart2;
                    } else if (reportType.reportType.toLowerCase().includes('user')) {
                      ReportIcon = Users;
                    }
                    
                    return (
                      <div
                        key={reportType.reportType}
                        id={`report-card-${index}`}
                        onClick={() => handleCardClick(index)}
                        className="bg-white flex items-center p-4 sm:p-5 border border-border rounded-card hover:shadow-md hover:border-primary transition-all text-left w-full cursor-pointer group"
                      >
                        <div id={`report-icon-${index}`} className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#FFE9E8] flex items-center justify-center mr-3 sm:mr-4 group-hover:bg-primary/20 transition-colors">
                          <ReportIcon className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                        </div>
                        <div className="flex-1">
                          <h3 id={`report-title-${index}`} className="font-heading text-subtext text-text-primary group-hover:text-primary-hover transition-colors">{reportType.reportType}</h3>
                          <p id={`report-description-${index}`} className="text-text-secondary text-body font-normal">
                            Generate a detailed report for {reportType.reportType.toLowerCase()}
                          </p>
                        </div>
                        <div className="text-text-secondary group-hover:text-primary transition-colors">
                          <ChevronRight id={`report-chevron-${index}`} className="h-4 w-4 sm:h-5 sm:w-5" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Reports;